/* istanbul ignore file */
import {
  cusorOverrideZoomInIcon,
  cusorOverrideZoomOutIcon,
} from '@noths/polaris-client-ribbons-design-system';
import { hideScrollbar, shadowRight, shadowUp } from '@noths/polaris-client-styles';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

import {
  ZOOM_PAN_CONTENT_CLASS,
  ZOOM_PAN_WRAPPER_CLASS,
} from 'src/components/organisms/ProductMediaCarousel/constants/elementIds';

const { colors, spacing } = themeVariables;
const THUMBNAILS_HEIGHT = 150;

/*
  The modal is a column of three rows: header, media, footer. Laid out as flex so each row
  measures itself and the media takes what is left over. It used to be sized by hand, with the
  media as calc(100% - a header constant - a footer constant), which meant two heights restated
  in this file that nothing enforced, and a third to add for the dots below, which are only there
  on a touchscreen.
*/
export const viewport = css`
  display: flex;
  flex-direction: column;
`;

export const header = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  padding: var(${spacing.md});
`;

export const fullScreenMedia = css`
  width: 100%;
  height: 100%;
  object-fit: contain;
  margin: 0 auto;
`;

/*
  The arrows are positioned against this, not against the track, because an absolutely positioned
  child of a scroll container scrolls away with the content.

  min-height: 0 because a flex item's default minimum is its content, which for a full size image
  would push the footer off the bottom of the screen instead of shrinking.
*/
export const mediaContainer = css`
  width: 100%;
  flex: 1;
  min-height: 0;
  position: relative;
`;

/*
  One scroll-snap track, the same mechanism as the gallery on the page, so the media can be swiped
  through here rather than only stepped through with the arrows.

  There is deliberately no touch-action declared. At scale 1 the zoom wrapper leaves a one finger
  drag alone, so the browser scrolls this track; once an image is zoomed the wrapper claims the
  drag to pan it and preventDefault stops the track moving. Declaring touch-action would take the
  swipe away at every scale.
*/
export const mediaTrack = css`
  position: relative;
  display: flex;
  height: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-behavior: auto;
  scroll-snap-type: x mandatory;

  ${hideScrollbar}

  /* After hideScrollbar, which sets overflow-y: scroll of its own. Explicit and last, so a
     sub-pixel rounding on a slide's height cannot make the track vertically scrollable and
     swallow a swipe meant to move through the media. */
  overflow-y: hidden;

  .${ZOOM_PAN_WRAPPER_CLASS}, .${ZOOM_PAN_CONTENT_CLASS} {
    height: 100%;
    width: 100%;
  }
`;

export const tabpanel = css`
  position: relative;
  height: 100%;
  min-width: 100%;
  scroll-snap-align: center;
  scroll-snap-stop: always;

  .${ZOOM_PAN_WRAPPER_CLASS}, .${ZOOM_PAN_CONTENT_CLASS} {
    height: 100%;
    width: 100%;
  }

  /*
    react-zoom-pan-pinch takes pointer events off the images it wraps, which also stops them
    being right clicked and copied. draggable={false} on the image does that job instead.
    Nested one class deeper than the library's rule, so injection order cannot decide it.
  */
  .${ZOOM_PAN_CONTENT_CLASS} img {
    pointer-events: auto;
  }
`;

export const keyboardNavigationText = css`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translate(-50%, 0);
  background-color: rgb(255, 255, 255);
  border-radius: 0.625rem;
  padding: 6px 0.5rem;
  margin: 0px;
  font-size: 0.75rem;
  border: none;

  &[data-has-focus='false'] {
    clip: rect(0, 0, 0, 0);
  }
`;

export const zoomWrapper = css`
  width: 100%;
  height: 100%;

  &[data-has-zoomed='true'] {
    ${cusorOverrideZoomOutIcon}
  }
  &[data-has-zoomed='false'] {
    ${cusorOverrideZoomInIcon}
  }
`;

/*
  The dots say the media can be swiped, which is only true of a touchscreen, so they follow the
  touchscreen and nothing else. They are an addition rather than a replacement: the thumbnail
  rail below stays at every size, because it says which media there are, and being able to pick
  one is worth having on a phone too.

  In the DOM at every size and hidden in CSS, the way the arrows are, so nothing has to be
  decided before the first paint. any-pointer rather than pointer for the reason given in
  ImageNavigationButtons.styles.ts: pointer describes only the primary input, which a touchscreen
  laptop reports as fine while still swiping perfectly well. The exact mirror of the arrows
  there, which a touchscreen loses because it can swipe instead.
*/
export const dots = css`
  display: none;
  flex-shrink: 0;
  padding-bottom: var(${spacing.md});

  @media (any-pointer: coarse) {
    display: block;
  }
`;

export const thumbnails = css`
  display: block;
  flex-shrink: 0;
  width: 100%;
  height: ${THUMBNAILS_HEIGHT}px;
  padding: var(${spacing.sm});
  background-color: var(${colors.surface.default});
  z-index: 1;
  ${shadowUp};
  ${shadowRight};
`;

export const imageNumberContainer = css`
  text-align: center;
  margin-bottom: var(${spacing.sm});
`;
